"use client";

import type { LessonHost } from "@/features/calendar/types/create-slot";
import type { AvailableRoles } from "@/types";

import { AnimatePresence, motion } from "motion/react";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { Icon } from "@iconify-icon/react";
import { Link } from "react-router-dom";

import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useCalendarModalStore } from "@/features/calendar/store/useCalendarModalStore";
import { useHostingOptions } from "@/features/calendar/types/hostingOptions";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale.ts";
import {
  slideUpAnimation,
  staggeredContainerAnimation,
} from "@/features/calendar/utils/slot-creation";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import GenericLoader from "@/components/GenericLoader";

export function SlotDetailsContent() {
  const { t } = useTranslation(["forms", "common", "calendar"]);
  const { slot, isLoading, setAction } = useCalendarModalStore();
  const { user } = useUserStore();

  const hostingOptions = useHostingOptions();

  if (isLoading) {
    return <GenericLoader />;
  }

  if (!slot) {
    return (
      <div className="flex flex-grow items-center justify-center p-6">
        <Card className="w-full max-w-md rounded-2xl border-none border-red-500/20 bg-red-500/10 shadow-2xl backdrop-blur-xl">
          <CardContent className="p-6 text-center">
            <Icon
              className="mx-auto mb-3 text-red-400"
              height="2rem"
              icon="tabler:alert-circle"
              width="2rem"
            />
            <p className="font-medium text-red-400">{t("common:calendar.slot.loadError")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isExpired = slot.status === "expired";
  const isBooked = slot.status === "booked";

  const startDateTime = parseISO(slot.startTimeDateTimeString);
  const endDateTime = parseISO(slot.endTimeDateTimeString);

  const formattedDate = format(startDateTime, "PPP", {
    locale: getDateFnsLocale(i18n.language),
  });
  const formattedStartTime = format(startDateTime, "p", {
    locale: getDateFnsLocale(i18n.language),
  });
  const formattedEndTime = format(endDateTime, "p", {
    locale: getDateFnsLocale(i18n.language),
  });
  const duration = (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60);

  const host = hostingOptions.find((h) => h.id === (slot.lessonHost as LessonHost));

  return (
    <ScrollArea className="h-[calc(100dvh_-_130px)]">
      <AnimatePresence mode="popLayout">
        <motion.div
          key="slot-details"
          animate="animate"
          className="space-y-5 p-5"
          exit="exit"
          initial="initial"
          variants={staggeredContainerAnimation}
        >
          {isBooked && slot.lesson && (
            <motion.div variants={slideUpAnimation}>
              <Link
                className="block"
                to={`/${user?.role === "tutor" ? "tutor" : "student"}/workspaces/${slot.lesson?.workspace_id ?? ""}/lessons/${slot.lesson?.id ?? ""}/details`}
              >
                <motion.div
                  className="bg-side-accent/50 text-accent-foreground hover:shadow-3xl relative overflow-hidden rounded-2xl border-none p-4 shadow-2xl backdrop-blur-xl transition-all"
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-5">
                    <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600" />
                    <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-gradient-to-tr from-blue-400 to-cyan-500" />
                  </div>

                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-muted/30 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                        <Icon
                          className="text-accent-foreground"
                          height="1.25rem"
                          icon="mdi:book"
                          width="1.25rem"
                        />
                      </div>
                      <div>
                        <p className="text-accent-foreground font-semibold">
                          {t("common:calendar.slot.viewLessonDetails")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon
                        className="text-accent-foreground transition-transform duration-300 group-hover:translate-x-1"
                        height="1.25rem"
                        icon="tabler:arrow-right"
                        width="1.25rem"
                      />
                    </div>
                  </div>
                </motion.div>
              </Link>
            </motion.div>
          )}

          {/* Time and Duration Section */}
          <motion.div variants={slideUpAnimation}>
            <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute -top-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-br from-purple-400 to-pink-600" />
                <div className="absolute -right-12 -bottom-12 h-24 w-24 rounded-full bg-gradient-to-tr from-orange-400 to-red-500" />
              </div>

              <CardContent className="relative p-6">
                <h3 className="text-accent-foreground mb-6 flex items-center gap-2 text-lg font-semibold">
                  <Icon height="1.25rem" icon="tabler:clock" width="1.25rem" />
                  {t("common:calendar.slot.timeDetails")}
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <motion.div
                    animate={{ opacity: 1 }}
                    className="bg-muted/20 hover:bg-muted/30 flex items-center gap-3 rounded-xl border-none p-4 backdrop-blur-sm transition-all"
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                    whileHover={{ scale: 1.02, boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)" }}
                  >
                    <div className="bg-muted/30 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                      <Icon
                        className="text-accent-foreground"
                        height="1.25rem"
                        icon="tabler:calendar"
                        width="1.25rem"
                      />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                        {t("common:calendar.slot.date")}
                      </p>
                      <p className="text-accent-foreground font-semibold">{formattedDate}</p>
                    </div>
                  </motion.div>

                  <motion.div
                    animate={{ opacity: 1 }}
                    className="bg-muted/20 hover:bg-muted/30 flex items-center gap-3 rounded-xl border-none p-4 backdrop-blur-sm transition-all"
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                    whileHover={{ scale: 1.02, boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)" }}
                  >
                    <div className="bg-muted/30 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                      <Icon
                        className="text-accent-foreground"
                        height="1.25rem"
                        icon="tabler:clock"
                        width="1.25rem"
                      />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                        {t("common:calendar.slot.time")}
                      </p>
                      <p className="text-accent-foreground font-semibold">
                        {formattedStartTime} - {formattedEndTime}
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    animate={{ opacity: 1 }}
                    className="bg-muted/20 hover:bg-muted/30 col-span-2 flex items-center gap-3 rounded-xl border-none p-4 backdrop-blur-sm transition-all"
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                    whileHover={{ scale: 1.02, boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)" }}
                  >
                    <div className="bg-muted/30 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                      <Icon
                        className="text-accent-foreground"
                        height="1.25rem"
                        icon="tabler:hourglass"
                        width="1.25rem"
                      />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                        {t("common:calendar.slot.duration")}
                      </p>
                      <p className="text-accent-foreground font-semibold">
                        {duration} {t("common:calendar.slot.minutes")}
                      </p>
                    </div>
                  </motion.div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Lesson Type and Hosting Method */}
          <motion.div variants={slideUpAnimation}>
            <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-violet-400 to-purple-600" />
                <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-indigo-400 to-blue-500" />
              </div>

              <CardContent className="relative p-6">
                <h3 className="text-accent-foreground mb-6 flex items-center gap-2 text-lg font-semibold">
                  <Icon height="1.25rem" icon="tabler:settings" width="1.25rem" />
                  {t("common:calendar.slot.configuration")}
                </h3>

                <div className="space-y-4">
                  <motion.div
                    animate={{ opacity: 1 }}
                    className="bg-muted/20 hover:bg-muted/30 flex items-center gap-3 rounded-xl border-none p-4 backdrop-blur-sm transition-all"
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                    whileHover={{ scale: 1.02, boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)" }}
                  >
                    <div className="bg-muted/30 flex size-12 items-center justify-center rounded-xl backdrop-blur-sm">
                      <Icon
                        className="text-accent-foreground"
                        height="1.5rem"
                        icon="tabler:users"
                        width="1.5rem"
                      />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                        {t("common:calendar.slot.lessonType")}
                      </p>
                      <p className="text-accent-foreground text-lg font-semibold capitalize">
                        {slot.lessonType}
                      </p>
                    </div>
                  </motion.div>

                  {host && (
                    <motion.div
                      animate={{ opacity: 1 }}
                      className="bg-muted/20 hover:bg-muted/30 flex items-center gap-3 rounded-xl border-none p-4 backdrop-blur-sm transition-all"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                      whileHover={{
                        scale: 1.02,
                        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <div className="bg-muted/30 flex size-12 items-center justify-center rounded-xl backdrop-blur-sm">
                        {host.icon}
                      </div>
                      <div className="flex-1">
                        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                          {t("common:calendar.slot.hostingMethod")}
                        </p>
                        <p className="text-accent-foreground text-lg font-semibold">{host.title}</p>
                        {host.description && (
                          <p className="text-muted-foreground mt-1 text-sm">{host.description}</p>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {"communicationDetails" in slot && slot.communicationDetails && (
                    <motion.div
                      animate={{ opacity: 1 }}
                      className="mt-4"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                    >
                      <h4 className="text-accent-foreground mb-3 flex items-center gap-2 text-base font-semibold">
                        <Icon height="1.25rem" icon="tabler:message-circle" width="1.25rem" />
                        {t("forms:hostMethodSelector.communicationDetails")}
                      </h4>
                      <p className="text-muted-foreground bg-muted/20 rounded-xl border-none p-4 backdrop-blur-sm">
                        {slot.communicationDetails}
                      </p>
                    </motion.div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Lesson Information (when booked) */}
          {isBooked && slot.lesson && (
            <motion.div variants={slideUpAnimation}>
              <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute -top-12 -left-12 h-24 w-24 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600" />
                  <div className="absolute -right-20 -bottom-20 h-40 w-40 rounded-full bg-gradient-to-tr from-green-400 to-emerald-500" />
                </div>

                <CardContent className="relative p-6">
                  <h3 className="text-accent-foreground mb-6 flex items-center gap-2 text-lg font-semibold">
                    <Icon height="1.25rem" icon="tabler:book" width="1.25rem" />
                    {t("common:calendar.slot.lessonDetails")}
                  </h3>

                  <div className="space-y-4">
                    {slot.lesson.subject && (
                      <motion.div
                        animate={{ opacity: 1 }}
                        className="bg-muted/20 hover:bg-muted/30 flex items-center gap-3 rounded-xl border-none p-4 backdrop-blur-sm transition-all"
                        initial={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                        whileHover={{
                          scale: 1.02,
                          boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <div className="bg-muted/30 flex size-12 items-center justify-center rounded-xl backdrop-blur-sm">
                          <Icon
                            className="text-accent-foreground"
                            height="1.5rem"
                            icon="tabler:school"
                            width="1.5rem"
                          />
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                            {t("common:calendar.slot.subject")}
                          </p>
                          <p className="text-accent-foreground text-lg font-semibold">
                            {slot.lesson.subject.name}
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {slot.lesson.topic && (
                      <motion.div
                        animate={{ opacity: 1 }}
                        className="bg-muted/20 hover:bg-muted/30 flex items-center gap-3 rounded-xl border-none p-4 backdrop-blur-sm transition-all"
                        initial={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                        whileHover={{
                          scale: 1.02,
                          boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <div className="bg-muted/30 flex size-12 items-center justify-center rounded-xl backdrop-blur-sm">
                          <Icon
                            className="text-accent-foreground"
                            height="1.5rem"
                            icon="tabler:bookmark"
                            width="1.5rem"
                          />
                        </div>
                        <div>
                          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                            {t("common:calendar.slot.topic")}
                          </p>
                          <p className="text-accent-foreground text-lg font-semibold">
                            {slot.lesson.topic}
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {slot.lesson.participants && slot.lesson.participants.length > 0 && (
                      <motion.div
                        animate={{ opacity: 1 }}
                        className="mt-6"
                        initial={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                      >
                        <h4 className="text-accent-foreground mb-4 flex items-center gap-2 text-base font-semibold">
                          <Icon height="1.25rem" icon="tabler:users-group" width="1.25rem" />
                          {t("common:calendar.slot.participants")}
                        </h4>
                        <div className="space-y-3">
                          {slot.lesson.participants.map((participant) => (
                            <div
                              key={participant.id}
                              className="bg-muted/20 hover:bg-muted/30 flex items-center gap-3 rounded-xl border-none p-3 backdrop-blur-sm transition-all"
                            >
                              <div className="relative">
                                <div
                                  className="size-10 rounded-xl bg-cover bg-center"
                                  style={{ backgroundImage: `url(${participant.avatar})` }}
                                />
                              </div>
                              <div>
                                <p className="text-accent-foreground font-semibold">
                                  {participant.name}
                                </p>
                                <p className="text-muted-foreground text-sm capitalize">
                                  {t(`common:roles.${participant.type as AvailableRoles}`)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Status Messages and Actions */}
          {isExpired && (
            <motion.div variants={slideUpAnimation}>
              <Card className="bg-muted/30 border-muted/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <Icon
                      className="text-muted-foreground"
                      height="1.5rem"
                      icon="tabler:clock-x"
                      width="1.5rem"
                    />
                    <div>
                      <p className="text-muted-foreground font-semibold">
                        {t("common:calendar.slot.expiredMessage")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Edit button for available slots */}
          {!isBooked && !isExpired && (
            <motion.div variants={slideUpAnimation}>
              <button
                className="w-full cursor-pointer appearance-none border-none bg-transparent p-0 text-left"
                onClick={() => {
                  setAction("edit");
                }}
              >
                <motion.div
                  className="bg-side-accent/50 text-accent-foreground hover:shadow-3xl relative overflow-hidden rounded-2xl border-none p-4 shadow-2xl backdrop-blur-xl transition-all"
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-5">
                    <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-gradient-to-br from-purple-400 to-indigo-600" />
                    <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-gradient-to-tr from-violet-400 to-purple-500" />
                  </div>

                  <div className="relative flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-muted/30 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                        <Icon
                          className="text-accent-foreground"
                          height="1.25rem"
                          icon="tabler:pencil"
                          width="1.25rem"
                        />
                      </div>
                      <div>
                        <p className="text-accent-foreground font-semibold">{t("common:edit")}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon
                        className="text-accent-foreground transition-transform duration-300 group-hover:translate-x-1"
                        height="1.25rem"
                        icon="tabler:arrow-right"
                        width="1.25rem"
                      />
                    </div>
                  </div>
                </motion.div>
              </button>
            </motion.div>
          )}

          <Separator className="bg-muted-foreground/10" />
        </motion.div>
      </AnimatePresence>
    </ScrollArea>
  );
}
